import { updateStaff, getOrgUnitsByLevel, addUserToUserGroup } from '../../../utils/api'
import { getAuthHeader } from '../../../utils/auth'
import { ROLE_LEVEL_MAP, ROLE_USERGROUP_MAP } from '../api/constants'
import { useDataEngine } from '@dhis2/app-runtime';

const BASE_URL = process.env.REACT_APP_DHIS2_BASE_URL || '';

const STAFF_ROLE_ID = 'R3lFolCMdoi';

export const staffListQuery = (programId) => ({
  staff: {
    resource: 'trackedEntityInstances',
    params: {
      program: programId,
      paging: 1000,
      fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
    }
  }
})


// Optional: Helper function to remove user from user group
async function removeUserFromUserGroup(userId, userGroupId, engine) {
  try {
    await engine.mutate({
      resource: `userGroups/${userGroupId}/users/${userId}`,
      type: 'delete'
    });
    return true;
  } catch (error) {
    console.error(`Error removing user ${userId} from group ${userGroupId}:`, error);
    throw error;
  }
}

// Merged update function for user and TEI updates

export async function updateStaffComplete(engine, userId, teiId, staffData, auth) {
  try {
    console.log('🔧 updateStaffComplete called with:', { userId, teiId, staffData });

    // ✅ DEBUG: Check user groups BEFORE any changes
    await debugUserGroups(engine, userId, 'BEFORE updateStaffComplete');

    if (!userId) throw new Error('User ID is required for update');

    // ✅ FIXED: Handle multiple user roles (not just single userRole)
    if (staffData.userRoles && Array.isArray(staffData.userRoles) && staffData.userRoles.length > 0) {
      console.log('🎯 Processing user roles:', staffData.userRoles);

      // Get current user groups
      const currentUserGroups = await getUserUserGroups(userId, engine);
      console.log('📋 Current user groups:', currentUserGroups.map(g => `${g.name} (${g.id})`));

      // Map roles to user groups
      const targetUserGroupIds = staffData.userRoles
        .map(roleId => ROLE_USERGROUP_MAP[roleId])
        .filter(groupId => groupId); // Remove undefined/null

      console.log('🎯 Target user groups from roles:', targetUserGroupIds);

      // Add user to needed groups
      for (const userGroupId of targetUserGroupIds) {
        const isAlreadyInGroup = currentUserGroups.some(group => group.id === userGroupId);

        if (!isAlreadyInGroup) {
          console.log(`🔄 Adding user to user group: ${userGroupId}`);
          await addUserToUserGroup(userId, userGroupId, engine);
          console.log(`✅ User ${userId} added to user group ${userGroupId}`);
        } else {
          console.log(`✅ User ${userId} already in user group ${userGroupId}`);
        }
      }

      // Remove from groups not in new roles (optional - uncomment if you want this behavior)
      // for (const currentGroup of currentUserGroups) {
      //   if (!targetUserGroupIds.includes(currentGroup.id)) {
      //     console.log(`🔄 Removing user from user group: ${currentGroup.id}`);
      //     await removeUserFromUserGroup(userId, currentGroup.id, engine);
      //     console.log(`✅ User ${userId} removed from user group ${currentGroup.id}`);
      //   }
      // }
    }

    // ✅ CRITICAL FIX: Check if TEI exists - handle staff without TEI gracefully
    console.log('🔍 Checking TEI existence before update...');
    let teiExists = false;
    let currentTeiData = null;

    if (teiId) {
      try {
        const teiCheckUrl = `${BASE_URL}/api/trackedEntityInstances/${teiId}?fields=attributes[attribute,value],orgUnit`;
        const teiCheckResponse = await fetch(teiCheckUrl, {
          headers: getAuthHeader(auth),
        });

        if (teiCheckResponse.ok) {
          currentTeiData = await teiCheckResponse.json();
          teiExists = true;
          console.log('✅ TEI found - staff has existing TEI record');
        } else {
          console.log('⚠️ TEI not found - this staff member does not have a TEI record');
          teiExists = false;
        }
      } catch (error) {
        console.log('⚠️ TEI check failed - staff likely does not have TEI record:', error.message);
        teiExists = false;
      }
    } else {
      console.log('ℹ️ No TEI ID provided - staff does not have TEI record');
      teiExists = false;
    }

    // ✅ DEBUG: Check user groups AFTER user update but BEFORE TEI update
    await debugUserGroups(engine, userId, 'AFTER user update');

    // Get current user's org units BEFORE updating
    const { user: currentUser } = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: { fields: 'organisationUnits[id]' }
      }
    });

    const existingOrgUnits = currentUser.organisationUnits || [];
    let finalOrgUnits = [...existingOrgUnits];

    // ==================== CRITICAL: Handle department (org unit) update ====================
    // If staffData has organisationUnits (multiple), use them
    if (staffData.organisationUnits && Array.isArray(staffData.organisationUnits) && staffData.organisationUnits.length > 0) {
      console.log('🏢 Setting multiple org units:', staffData.organisationUnits.map(ou => ou.id));

      // Use the provided organisationUnits array (includes both selected and top-level)
      finalOrgUnits = [...staffData.organisationUnits];
    } else if (staffData.orgUnit) {
      console.log('🏢 Setting single department/org unit:', staffData.orgUnit);

      // Fallback to single org unit for backward compatibility
      // Remove any existing org units to avoid conflicts
      finalOrgUnits = [{ id: staffData.orgUnit }];
    } else {
      console.log('⚠️ No department/org unit specified in staffData');
    }

    // ==================== CRITICAL: Prepare user update payload with roles ====================
    const userPayload = {
      firstName: staffData.firstName,
      surname: staffData.secondName,
      email: staffData.email,
      phoneNumber: staffData.phoneNumber,
      organisationUnits: finalOrgUnits, // Use the updated list
      // IMPORTANT: Include userRoles in the payload for updateStaff function
      userRoles: staffData.userRoles || (staffData.userRole ? [staffData.userRole] : []),
      attributeValues: staffData.dob ? [{ attribute: 'Ndsp9fQExRc', value: staffData.dob }] : [],
    };

    console.log('📦 User update payload:', userPayload);

    const userUpdateResponse = await updateStaff(userId, teiId, userPayload, auth);

    if (!userUpdateResponse) {
      throw new Error('Failed to update user account');
    }

    // ✅ CRITICAL FIX: Update TEI with department information
    let teiUpdateResponse = null;

    if (teiExists && currentTeiData) {
      // Update existing TEI with username, gender, AND department
      console.log('🔗 Updating existing TEI with attributes (username, gender, department)...');

      const existingAttributes = currentTeiData.attributes || [];

      // Preserve existing attributes except the ones we're updating
      const preservedAttributes = existingAttributes.filter(attr =>
        !['g3RRIfDiQra', 'n8aAqQTVI8b', 'HAOvVNYm8Lx', 'HZG6mhL4FL5'].includes(attr.attribute)
      );

      // Build TEI attributes array
      const teiAttributes = [
        // CRITICAL: User ID linkage
        { attribute: 'n8aAqQTVI8b', value: userId },
        // Username linkage for searching
        { attribute: 'g3RRIfDiQra', value: staffData.username },
        // Gender stored in TEI
        { attribute: 'HAOvVNYm8Lx', value: staffData.gender },
      ];

      // ==================== CRITICAL: Add department attribute ====================
      if (staffData.department) {
        // If department is explicitly provided, use it
        teiAttributes.push({ attribute: 'HZG6mhL4FL5', value: staffData.department });
      } else if (staffData.orgUnitName) {
        // Otherwise use the org unit name as department
        teiAttributes.push({ attribute: 'HZG6mhL4FL5', value: staffData.orgUnitName });
      }

      const teiPayload = {
        trackedEntityInstance: teiId,
        trackedEntityType: "OaAbpIs1cV7",
        // CRITICAL: Update TEI org unit to match user's department
        orgUnit: staffData.orgUnit || currentTeiData.orgUnit,
        attributes: [
          ...teiAttributes,
          ...preservedAttributes
        ].filter(attr => attr.value !== undefined && attr.value !== null && attr.value !== '')
      };

      console.log('🔄 TEI update payload (with department):', teiPayload);

      const teiUpdateResult = await fetch(`${BASE_URL}/api/trackedEntityInstances/${teiId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(auth),
        },
        credentials: 'include',
        body: JSON.stringify(teiPayload),
      });

      if (!teiUpdateResult.ok) {
        const errorText = await teiUpdateResult.text();
        console.error('❌ TEI update failed:', errorText);
        // Don't throw error - user update was successful, just log the TEI failure
        teiUpdateResponse = { success: false, error: errorText };
      } else {
        teiUpdateResponse = { success: true };
        console.log('✅ TEI update successful - attributes including department stored');
      }
    } else if (!teiExists) {
      // Create TEI if it doesn't exist
      console.log('📝 Creating new TEI for staff...');

      try {
        const teiPayload = {
          trackedEntityType: "OaAbpIs1cV7",
          orgUnit: staffData.orgUnit || userPayload.organisationUnits[0]?.id,
          attributes: [
            { attribute: 'n8aAqQTVI8b', value: userId },
            { attribute: 'g3RRIfDiQra', value: staffData.username },
            { attribute: 'HAOvVNYm8Lx', value: staffData.gender },
          ]
        };

        // Add department if available
        if (staffData.department || staffData.orgUnitName) {
          teiPayload.attributes.push({
            attribute: 'HZG6mhL4FL5',
            value: staffData.department || staffData.orgUnitName
          });
        }

        const createTeiResponse = await fetch(`${BASE_URL}/api/trackedEntityInstances`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(auth),
          },
          body: JSON.stringify(teiPayload),
        });

        if (createTeiResponse.ok) {
          const newTei = await createTeiResponse.json();
          console.log('✅ New TEI created:', newTei);
          teiUpdateResponse = { success: true, newTei };
        } else {
          const errorText = await createTeiResponse.text();
          console.error('❌ TEI creation failed:', errorText);
          teiUpdateResponse = { success: false, error: errorText };
        }
      } catch (teiError) {
        console.error('❌ TEI creation error:', teiError);
        teiUpdateResponse = { success: false, error: teiError.message };
      }
    }

    console.log('✅ updateStaffComplete completed successfully');

    // ✅ DEBUG: Check user groups at the very end
    await debugUserGroups(engine, userId, 'FINAL after updateStaffComplete');

    return {
      user: userUpdateResponse,
      tei: teiUpdateResponse,
      teiExists: teiExists,
      note: 'User, roles, and department updated successfully'
    };
  } catch (error) {
    console.error('❌ updateStaffComplete error:', error);
    throw new Error(`Failed to update staff: ${error.message}`);
  }
}

// Add this debug function to track user group changes
const debugUserGroups = async (engine, userId, operation) => {
  try {
    const response = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: {
          fields: 'id,username,userGroups[id,name]'
        }
      }
    });

    const userGroups = response.user.userGroups || [];
    console.log(`🔍 DEBUG ${operation}: User ${userId} has ${userGroups.length} user groups:`,
      userGroups.map(g => `${g.name} (${g.id})`).join(', '));

    return userGroups;
  } catch (error) {
    console.error(`🔍 DEBUG ${operation} failed:`, error);
    return [];
  }
};

// ✅ ADD THIS FUNCTION: Get user's current user groups
async function getUserUserGroups(userId, engine) {
  try {
    const response = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: {
          fields: 'userGroups[id,name]'
        }
      }
    });
    return response.user.userGroups || [];
  } catch (error) {
    console.error('Error fetching user groups:', error);
    return [];
  }
}

// ✅ ADD THIS FUNCTION: Get parent org unit for search visibility
export const getParentOrgUnit = async (engine, orgUnitId) => {
  try {
    const response = await engine.query({
      orgUnit: {
        resource: 'organisationUnits',
        id: orgUnitId,
        params: {
          fields: 'id,name,parent[id,name]'
        }
      }
    });

    return response.orgUnit.parent;
  } catch (error) {
    console.error('Error fetching parent org unit:', error);
    return null;
  }
};


// Delete a staff member
export const deleteStaff = {
  resource: 'trackedEntityInstances',
  type: 'delete',
  id: ({ id }) => id
}

export const usersQuery = {
  users: {
    resource: 'users',
    params: {
      fields: 'id,displayName,userCredentials[username]',
      paging: 1000
    }
  }
}

export const staffUsersQuery = {
  staffUsers: {
    resource: 'users',
    params: {
      fields: 'id,username,firstName,surname,email,userCredentials[username,userRoles]',
      filter: `userCredentials.userRoles.id:eq:${STAFF_ROLE_ID}`,
      pageSize: 1000
    }
  },
  staffTEIs: {
    resource: 'trackedEntityInstances',
    params: {
      fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
      trackedEntityType: 'OaAbpIs1cV7',
      filter: 'n8aAqQTVI8b:!null', // Filter for TEIs with user ID attribute
      pageSize: 1000
    }
  }
}

// Add this function near your other utility functions
export const validateUserMovement = async (engine, userId, targetOrgUnitId, currentOrgUnitId) => {
  console.log('✅ Movement always allowed - Objectives handled via Position TEI');
  return {
    canMove: true,
    reason: 'Movement allowed - Objectives managed through Position TEI system'
  };
};


// Optional: wrap staffListQuery if we want to use a function called fetchStaffList
export const fetchStaffList = staffListQuery
